import { createContext, useContext, useState } from 'react';
import {
	agendasAisQuery,
	aisQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { GovError, useExtraConfigContext } from '@gov-nx/core/app';
import type { Nullable } from '@gov-nx/core/types';
import { pipe } from '@gov-nx/core/types';
import type {
	AisItem,
	KdeOMneStatVedeUdajeContextProviderProps,
	KdeOMneStatVedeUdajeContextValue,
	KdeOMneStatVedeUdajeData,
} from './context.types';
import { prepareAgendasResponse } from './utils';

const KdeOMneStatVedeUdajeContext =
	createContext<Nullable<KdeOMneStatVedeUdajeContextValue>>(null);

export const KdeOMneStatVedeUdajeContextProvider = ({
	children,
}: KdeOMneStatVedeUdajeContextProviderProps) => {
	const [expandedAis, setExpandedAis] = useState<AisItem['kodAis']>();
	const {
		extraConfig: { environment },
	} = useExtraConfigContext();
	const { axiosInstance } = useAxiosInstance();

	const sliceDataOnLclAndAce = (data: KdeOMneStatVedeUdajeData) => {
		if (!data) return null;
		if (['lcl', 'ace'].includes(`${environment}`)) {
			return data?.slice(0, 3);
		}
		return data;
	};

	const query = usePoQuery({
		queryKey: ['agendy-ais'],
		queryFn: async () => agendasAisQuery(axiosInstance),
		refetchOnWindowFocus: false,
	});

	const aisDataQuery = usePoQuery({
		queryKey: ['agendy-ais', expandedAis],
		queryFn: async () => aisQuery(axiosInstance, expandedAis ?? null),
		refetchOnWindowFocus: false,
		enabled: !!expandedAis,
	});

	const toggleAis = (kodAis?: string) => {
		if (!kodAis || kodAis === expandedAis) {
			setExpandedAis(undefined);
		} else {
			setExpandedAis(kodAis);
		}
	};

	return (
		<KdeOMneStatVedeUdajeContext.Provider
			value={{
				data: pipe(
					query.data?.seznam ?? [],
					prepareAgendasResponse,
					sliceDataOnLclAndAce
				),
				controls: {
					initialLoading: query.isLoading,
					initialError: query.error ? new GovError(query.error.message) : null,
					processLoading: false,
					processError: null,
				},
				expandedAis,
				toggleAis,
				aisData: aisDataQuery.data,
			}}>
			{children}
		</KdeOMneStatVedeUdajeContext.Provider>
	);
};

export const useKdeOMneStatVedeUdajeContext = () =>
	useContext(KdeOMneStatVedeUdajeContext) as KdeOMneStatVedeUdajeContextValue;
